import type { CSSProperties, ReactElement } from 'react';
import type { ProposalBuildPage } from '@shared/api';
import type { Proposal } from '@shared/types';
import { PageFrame } from '../../pages/Page';
import { PageArchetype } from '../../pages/registry';
import { READ_ONLY_ACTIONS } from '../../pages/types';
import { ScrollText } from './ScrollText';

const pad2 = (value: number): string => String(value).padStart(2, '0');

/** Stable per-id pseudo-random in [0, 1) — no Math.random so re-renders never flicker. */
function seeded(id: string, salt: number): number {
  let hash = salt;
  for (let index = 0; index < id.length; index += 1) hash = (hash * 31 + id.charCodeAt(index)) >>> 0;
  return (hash % 1000) / 1000;
}

/** A page type that reads as image/hero-led in its "assembling" primitives. */
function isVisualType(type: ProposalBuildPage['type']): boolean {
  return (
    type === 'cover' ||
    type === 'product_divider' ||
    type === 'campaign_goal' ||
    type === 'why_us' ||
    type === 'thank_you' ||
    type === 'media_mix' ||
    type === 'budget_summary' ||
    type === 'projections' ||
    type === 'stats'
  );
}

/** Layout-only mini preview standing in for a page that has no renderable content yet. */
function AssemblingPrimitives({ page }: { page: ProposalBuildPage }): ReactElement {
  const lineCount = 3 + Math.floor(seeded(page.id, 1) * 3); // 3..5
  const lines = Array.from({ length: lineCount }, (_, index) => {
    const width = 34 + Math.round(seeded(page.id, index + 7) * 46); // 34%..80%
    return width;
  });
  const visual = isVisualType(page.type);

  return (
    <div className="build-page-primitives" aria-hidden="true">
      <span className="build-page-primitives-title" />
      <span className="build-page-primitives-rule" />
      <div className="build-page-primitives-body">
        {visual ? (
          <div className="build-page-primitives-hatched-row">
            <span className="build-page-primitives-hatched" />
            <span className="build-page-primitives-hatched" />
          </div>
        ) : null}
        <div className="build-page-primitives-lines">
          {lines.map((width, index) => (
            <span key={index} className="build-page-primitives-line" style={{ width: `${width}%` }} />
          ))}
        </div>
      </div>
    </div>
  );
}

export function BuildPageCard({
  page,
  proposal,
  revealDelayMs,
  onAttentionClick,
  onRetry,
  onFocus,
  reviewCount = 0,
  placeholder,
}: {
  page: ProposalBuildPage;
  proposal: Proposal;
  /** Set only for pages newly leaving `planned` in this snapshot — drives the one-time entrance. */
  revealDelayMs?: number;
  onAttentionClick?: (page: ProposalBuildPage) => void;
  onRetry?: (page: ProposalBuildPage) => void;
  onFocus?: (page: ProposalBuildPage) => void;
  reviewCount?: number;
  placeholder?: string;
}): ReactElement {
  const section = proposal.document?.sections.find((candidate) => candidate.id === page.id);
  const canShowThumbnail =
    Boolean(section) &&
    (page.status === 'content_ready' ||
      page.status === 'image_pending' ||
      page.status === 'checking' ||
      page.status === 'attention' ||
      page.status === 'ready');

  const classes = [
    'build-page-card',
    `build-page-card-${page.status}`,
    revealDelayMs !== undefined ? 'build-page-card-revealing' : '',
  ]
    .filter(Boolean)
    .join(' ');

  const style = revealDelayMs !== undefined ? ({ '--reveal-delay': `${revealDelayMs}ms` } as CSSProperties) : undefined;

  return (
    <article
      id={`build-page-${page.id}`}
      className={classes}
      style={style}
      role="listitem"
      onClick={onFocus ? () => onFocus(page) : undefined}
    >
      <div className="build-page-card-frame">
        {page.status === 'planned' ? (
          <><span className="build-page-card-number">{pad2(page.order)}</span>{placeholder ? <span className="build-page-card-placeholder">{placeholder}</span> : null}</>
        ) : canShowThumbnail && section ? (
          <PageFrame ariaLabel={page.title}>
            <PageArchetype proposal={proposal} section={section} actions={READ_ONLY_ACTIONS} readOnly />
          </PageFrame>
        ) : (
          <AssemblingPrimitives page={page} />
        )}
        {page.status === 'error' ? (
          <button
            type="button"
            className="build-page-card-retry"
            onClick={(event) => {
              event.stopPropagation();
              onRetry?.(page);
            }}
          >
            Retry page
          </button>
        ) : null}
      </div>
      {page.status === 'planned' ? (
        <div className="build-page-card-caption build-page-card-caption-placeholder" aria-hidden="true">
          <span className="build-page-card-caption-pill build-page-card-caption-pill-number" />
          <span className="build-page-card-caption-pill build-page-card-caption-pill-title" />
        </div>
      ) : (
        <div className="build-page-card-caption">
          <span className="build-page-card-caption-number">{pad2(page.order)}</span>
          <ScrollText className="build-page-card-caption-title">{page.title}</ScrollText>
          {reviewCount > 0 ? (
            <button
              type="button"
              className="build-page-card-review-badge"
              aria-label={`Review ${reviewCount} ${reviewCount === 1 ? 'item' : 'items'} on ${page.title}`}
              onClick={(event) => {
                event.stopPropagation();
                onAttentionClick?.(page);
              }}
            >{reviewCount}</button>
          ) : null}
        </div>
      )}
    </article>
  );
}
